<template lang="html">
    <!-- Show the room -->
    <div class="d-flex flex-column flex-md-row vh-lg-100">
        <!-- Side section -->
        <aside class="bg-white p-4 p-md-5 d-flex flex-column side-nav">
            <lobby-members class="mb-md-5"></lobby-members>
            <div class="mt-auto d-none d-md-block">
                <Rules class="mb-md-5"></Rules>
                <lobby-history></lobby-history>
            </div>
        </aside>

        <!-- Main section -->
        <div class="d-flex flex-grow-1 py-5 px-md-5 w-100 vh-lg-100"
             :class="`status-${room.status}`">
            <!-- Lobby  -->
            <div v-if="room.status !== 'started'"
                 class="container-fluid align-self-center">
                <img style="width: 300px;"
                     class="mx-auto img-fluid d-block mb-5 pb-5"
                     :src="require('@/assets/images/logo/logo-vertical-color.svg')
                         "
                     alt="Ring Of Fire" />
                <div class="row align-items-center justify-content-center">
                    <div class="col-12 col-lg-6 d-flex flex-column align-items-center">
                        <div class="h2 text-center font-weight-bold mb-3">
                            Your room code is:
                        </div>
                        <div class="h1 text-primary text-center font-weight-bold rounded-lg bg-light mb-5 p-3 px-5"
                             v-text="room.code"></div>
                        <b-button class="mt-5"
                                  variant="primary"
                                  size="lg"
                                  @click.prevent="startGame()"
                                  v-if="isUserHost">Start game</b-button>
                        <div v-else
                             class="text-center"
                             :class="{ invisible: !room.host }">
                            Waiting for the host ({{ room.host.name }}) to
                            start...
                        </div>
                    </div>
                </div>
            </div>

            <!-- Game -->
            <div v-else-if="room.status === 'started'"
                 class="container-fluid d-flex flex-column justify-content-center">
                <div class="d-flex align-items-center justify-content-center flex-column flex-grow-1 flex-nowrap"
                     v-if="room.deck.length > 0">
                    <div class="w-100 flex-shrink-0 mb-4">
                        <h2 class="h1 text-center font-weight-bold"
                            v-if="room.turn.name">
                            {{ room.turn.name }}'s turn
                        </h2>
                    </div>
                    <div class="w-100 flex-grow-1 mb-5">
                        <card-deck @cardPicked="pickCard"
                                   class="mx-auto"></card-deck>
                    </div>
                    <div class="w-100 flex-shrink-0">
                        <div class="row align-items-center">
                            <div class="col-12 col-xl-6 text-center text-xl-left">
                                <div v-if="isUserTurn">
                                    <b-button variant="primary"
                                              size="lg"
                                              class="d-inline-flex text-nowrap mx-auto mx-xl-0 mb-4 mb-xl-0"
                                              @click.prevent="endTurn()"
                                              :disabled="!picked || turnEnded">
                                        <div v-if="!turnEnded"
                                             v-text="picked
                                                 ? 'End turn'
                                                 : 'Please pick a card'
                                                 "></div>
                                        <div class="px-4 py-2 d-flex align-items-center"
                                             v-else>
                                            <b-spinner small
                                                       class="mx-auto"
                                                       variant="light"
                                                       label="Loading..."></b-spinner>
                                        </div>
                                    </b-button>
                                </div>
                                <div v-else>
                                    <h2 class="h5 font-weight-bold mb-4 mb-xl-0"
                                        v-if="rule"
                                        v-text="rule.description"></h2>
                                </div>
                            </div>
                            <div class="col-12 col-xl-6 d-flex flex-column align-items-center align-items-xl-end">
                                <drawn-kings></drawn-kings>
                            </div>
                        </div>
                    </div>
                </div>
                <b-spinner v-else
                           class="mx-auto"
                           variant="light"
                           label="Loading..."></b-spinner>
            </div>
            <endgame></endgame>
        </div>
    </div>
</template>

<script>
import CustomWebsocket from '@/mixins/classes/CustomWebsocket.js';
import gtag from '@/mixins/gtag.js';

import CardDeck from '@/components/CardDeck';
import Rules from '@/components/Rules';
import DrawnKings from '@/components/DrawnKings';
import Endgame from '@/components/Endgame';
import LobbyHistory from '@/components/LobbyHistory';
import LobbyMembers from '@/components/LobbyMembers';

export default {
    name: 'RoomShow',
    components: {
        CardDeck,
        DrawnKings,
        Endgame,
        LobbyHistory,
        LobbyMembers,
        Rules,
    },
    data() {
        return {
            rule: {
                title: '',
                description: '',
            },
            turnEnded: false,
        };
    },
    computed: {
        room() {
            return this.$store.state.room;
        },
        revealedCount() {
            return this.$store.getters.GET_REVEALED_CARD_COUNT;
        },
        user() {
            return this.$store.state.user;
        },
        ws() {
            return this.$store.state.room.socket;
        },
        picked() {
            return this.$store.state.room.hasPicked;
        },
        isUserTurn() {
            return this.room.turn.player_id === this.user.player_id;
        },
        isUserHost() {
            return this.room.host.player_id === this.user.player_id;
        },
    },
    watch: {
        '$store.state.room.latestCard': {
            handler(val) {
                if (val) {
                    const rule = this.$store.getters.GET_RULE;
                    this.rule = rule || '';
                }
            },
            immediate: true,
        },
    },
    mounted() {
        document.title = `Room #${this.room.code}`; // Set the tab title
        this.setupWebsocket();
    },
    methods: {
        /**
         * Establish a new connection to the websocket
         */
        setupWebsocket() {
            // Create the websocket and store in state
            this.$store.commit('SET_ROOM_ATTRIBUTES', {
                socket: new CustomWebsocket(this.room.websocketPath),
            });

            // Tell the socket we've connected when it opens
            this.ws.onopen = () => this.ws.write('player_connect', this.user);

            // Player connects or player disconnects
            this.ws.on(['player_connected', 'player_disconnected'], (data) => {
                // Save the players and the host
                const players = data.players;
                const host = players.find((player) => player.host === true);
                this.$store.commit('SET_ROOM_ATTRIBUTES', {
                    players: players,
                    host: host,
                });

                // If the game is started get the info and history
                if (this.room.status !== 'lobby') {
                    this.ws.write('game_info');
                    this.ws.write('game_history');
                }
            });

            // Game info
            this.ws.on('game_info', (data) => {
                const turn = this.$store.state.room.players.find(
                    (player) => player.player_id === data.player,
                );
                this.$store.commit('SET_ROOM_ATTRIBUTES', {
                    deck: data.deck,
                    turn: turn,
                });
            });

            // Game history
            this.ws.on('game_history', (data) => {
                this.$store.commit('SET_ROOM_ATTRIBUTES', {
                    history: data.history,
                });
            });

            // Card Revealed
            this.ws.on('card_revealed', (data) => {
                this.$store.commit('SET_ROOM_ATTRIBUTES', {
                    latestCard: data.card,
                });

                this.ws.write('player_list');
                this.ws.write('game_history');

                // Log the event with GA4
                gtag('event', 'card_revealed', {
                    card_value: data.card.identifier,
                    card_name: data.card.name,
                    card_index: data.number_of_pulled_cards || 'N/A',
                });
            });

            // Game started
            this.ws.on('game_started', (data) => {
                this.$store.commit('SET_ROOM_ATTRIBUTES', {
                    status: 'started',
                    deck: data.deck,
                    turn: data.player,
                });
            });

            // Player list
            this.ws.on('player_list', (data) => {
                this.$store.commit('SET_ROOM_ATTRIBUTES', {
                    players: data.players,
                });
            });

            // Next turn
            this.ws.on('next_turn', (data) => {
                this.turnEnded = false;
                this.$bus.$emit('clearCard');
                this.$store.commit('SET_ROOM_ATTRIBUTES', {
                    turn: data.player,
                });
                this.$store.commit('SET_PICKED', false);
            });

            // Game ended
            this.ws.on('game_ended', () => {
                /**
                 * @todo Remove this messy 💩 when state managements gets cleaned up
                 */
                this.$store.commit('SET_ROOM_ATTRIBUTES', {
                    code: '',
                    name: '',
                    status: '',
                    websocketPath: '',
                    socket: '',
                    history: [],
                    turn: '',
                    host: '',
                    players: [],
                    deck: [],
                    kings: [],
                    latestCard: '',
                    hasPicked: false,
                });
                this.$router.go();
            });

            // On error
            this.ws.onerror = (error) => console.log(error);
        },
        startGame() {
            this.ws.write('game_start');
        },
        endTurn() {
            this.turnEnded = true;
            this.ws.write('turn_end');
        },
        pickCard(data) {
            this.$store.commit('SET_PICKED', true);
            this.ws.write('reveal_card', { card_id: data });
        },
        toast(toaster, append = false, message) {
            this.toastCounter++;
            this.$bvToast.toast(message, {
                toaster: toaster,
                solid: true,
                appendToast: append,
                noCloseButton: true,
                toastClass: 'custom-toast-dark',
            });
        },
    },
};
</script>
